import { createContext, useCallback, useContext, useRef, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";

export type BulkAction = {
  index: number;
  title: string;
  apply: Record<string, unknown>;
  raw: unknown; // full action payload passed alongside
};

export type BulkResult = {
  index: number;
  title: string;
  ok: boolean;
  error?: { message: string; status?: number; raw?: string };
};

type Status = "idle" | "running" | "done";

type Ctx = {
  status: Status;
  total: number;
  done: number;
  currentTitle: string | null;
  results: BulkResult[];
  minimized: boolean;
  recapOpen: boolean;
  start: (actions: BulkAction[]) => void;
  cancel: () => void;
  setMinimized: (v: boolean) => void;
  openRecap: () => void;
  closeRecap: () => void;
  reset: () => void;
  onCompleteRef: React.MutableRefObject<((results: BulkResult[]) => void) | null>;
};

const SeoBulkApplyContext = createContext<Ctx | null>(null);

export function SeoBulkApplyProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>("idle");
  const [total, setTotal] = useState(0);
  const [done, setDone] = useState(0);
  const [currentTitle, setCurrentTitle] = useState<string | null>(null);
  const [results, setResults] = useState<BulkResult[]>([]);
  const [minimized, setMinimized] = useState(false);
  const [recapOpen, setRecapOpen] = useState(false);
  const cancelRef = useRef(false);
  const runningRef = useRef(false);
  const onCompleteRef = useRef<((results: BulkResult[]) => void) | null>(null);

  const start = useCallback((actions: BulkAction[]) => {
    if (runningRef.current || actions.length === 0) return;
    runningRef.current = true;
    cancelRef.current = false;
    setStatus("running");
    setTotal(actions.length);
    setDone(0);
    setResults([]);
    setMinimized(false);
    setRecapOpen(false);
    setCurrentTitle(actions[0].title);

    (async () => {
      const collected: BulkResult[] = [];
      for (let k = 0; k < actions.length; k++) {
        if (cancelRef.current) break;
        const a = actions[k];
        setCurrentTitle(a.title);
        try {
          const { data, error } = await supabase.functions.invoke(
            "seo-apply-action",
            { body: { apply: a.apply, action: a.raw } }
          );
          if (error) {
            let detail = error.message;
            let raw: string | undefined;
            let status: number | undefined;
            try {
              const ctx = (error as any).context;
              status = ctx?.status;
              if (ctx && typeof ctx.text === "function") {
                raw = await ctx.text();
                try {
                  const parsed = JSON.parse(raw!);
                  detail = parsed.error || parsed.detail || parsed.message || raw!;
                } catch {
                  detail = raw!;
                }
              }
            } catch { /* ignore */ }
            collected.push({ index: a.index, title: a.title, ok: false, error: { message: detail, status, raw } });
          } else if ((data as any)?.error) {
            collected.push({ index: a.index, title: a.title, ok: false, error: { message: (data as any).error } });
          } else {
            collected.push({ index: a.index, title: a.title, ok: true });
          }
        } catch (e: any) {
          collected.push({ index: a.index, title: a.title, ok: false, error: { message: e?.message ?? "Erreur inconnue" } });
        }
        setDone(k + 1);
        setResults([...collected]);
      }
      setStatus("done");
      setCurrentTitle(null);
      setRecapOpen(true);
      runningRef.current = false;
      onCompleteRef.current?.(collected);
    })();
  }, []);

  const cancel = useCallback(() => {
    cancelRef.current = true;
  }, []);

  const reset = useCallback(() => {
    if (runningRef.current) return;
    setStatus("idle");
    setTotal(0);
    setDone(0);
    setResults([]);
    setCurrentTitle(null);
    setRecapOpen(false);
  }, []);

  return (
    <SeoBulkApplyContext.Provider
      value={{
        status, total, done, currentTitle, results, minimized, recapOpen,
        start, cancel, setMinimized,
        openRecap: () => setRecapOpen(true),
        closeRecap: () => setRecapOpen(false),
        reset,
        onCompleteRef,
      }}
    >
      {children}
    </SeoBulkApplyContext.Provider>
  );
}

export function useSeoBulkApply() {
  const ctx = useContext(SeoBulkApplyContext);
  if (!ctx) throw new Error("useSeoBulkApply must be used within SeoBulkApplyProvider");
  return ctx;
}
